import { FieldType } from 'api/models/FieldTemplate';
import { produce } from 'immer';
import {
  getNewFieldItem,
  getNewInputLayoutItem,
  getNewLayoutItem,
} from 'routes/FormTemplateEditor/FormEditor/utils';
import { DialogTypes } from 'types/types';
import { getId } from 'utils';
import { insert } from 'object-path';
import {
  dialogState,
  fieldTemplatesState,
  lastAddedLayoutItem,
  layoutSelector,
} from 'state';
import { useRecoilState, useSetRecoilState } from 'recoil';

interface UseAddFieldTemplateProps {
  path: string;
  insertIndex?: number;
}

export default function useAddFieldTemplate({
  path,
  insertIndex,
}: UseAddFieldTemplateProps) {
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const setLatestAddedLayoutItem = useSetRecoilState(lastAddedLayoutItem);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);
  const openDialog = useSetRecoilState(dialogState);
  return async function ({ type }: { type: FieldType }) {
    const layoutItemId = getId();
    const fieldItemId = getId();

    switch (type) {
      case FieldType.Layout:
      case FieldType.Header:
      case FieldType.Divider:
      case FieldType.TableSelect:
        const layoutContainer = getNewLayoutItem({
          type,
        });
        setLatestAddedLayoutItem(layoutContainer);

        setLayout(
          produce(layout, draft =>
            insert(draft, path, layoutContainer, insertIndex ?? 0),
          ),
        );

        type === FieldType.Header &&
          openDialog({
            isOpen: true,
            dialogType: DialogTypes.editHeaderItem,
            data: {
              layoutItem: layoutContainer,
              path: `${path}.${insertIndex}`,
            },
          });
        break;

      case FieldType.Text:
      case FieldType.Number:
      case FieldType.Date:
      case FieldType.Calculated:
      case FieldType.Select:
      case FieldType.Combined:
      case FieldType.File:
      default:
        const newLayoutItem = getNewInputLayoutItem({
          id: layoutItemId,
          fieldTemplateId: fieldItemId,
        });
        setLatestAddedLayoutItem(newLayoutItem);
        const newField = getNewFieldItem(type, fieldItemId, layoutItemId);
        setFieldTemplates([...fieldTemplates, newField]);

        openDialog({
          isOpen: true,
          dialogType: DialogTypes.addFieldDialog,
          data: {
            fieldTemplate: newField,
            layoutItem: newLayoutItem,
            path: `${path}.${insertIndex}`,
          },
        });
        setLayout(
          produce(layout, draft =>
            insert(draft, path, newLayoutItem, insertIndex ?? 0),
          ),
        );
    }
  };
}
